<template>
  <div class="external-approvals p-3 p-md-4" data-test="external-approvals">
    <div
      class="d-flex flex-wrap justify-content-between align-items-start mb-3"
    >
      <div class="mb-2">
        <h5 class="font-weight-bold mb-1">External Approvals</h5>
        <p class="text-muted mb-0 intro">
          Weekly timesheets your tradies sent to a supervisor outside the
          company. Finalise an approved sheet to release its hours to payroll.
        </p>
      </div>
      <div class="d-flex align-items-center header-actions">
        <span class="text-muted small mr-2">Export</span>
        <b-button
          variant="outline-primary"
          size="sm"
          class="mr-1"
          :disabled="exporting || !visible.length"
          data-test="export-csv"
          @click="exportList('csv')"
        >
          CSV
        </b-button>
        <b-button
          variant="outline-primary"
          size="sm"
          class="mr-2"
          :disabled="exporting || !visible.length"
          data-test="export-pdf"
          @click="exportList('pdf')"
        >
          PDF
        </b-button>
        <b-button
          variant="outline-secondary"
          size="sm"
          :disabled="loading"
          data-test="refresh"
          @click="load"
        >
          Refresh
        </b-button>
      </div>
    </div>

    <div class="pills mb-3" role="group" aria-label="Filter">
      <button
        v-for="f in filters"
        :key="f.key"
        type="button"
        class="pill"
        :class="{ 'pill--on': filter === f.key }"
        :aria-pressed="filter === f.key"
        :data-test="`filter-${f.key}`"
        @click="setFilter(f.key)"
      >
        {{ f.label }}
        <span class="pill__count" :data-test="`count-${f.key}`">{{
          counts[f.key]
        }}</span>
      </button>
    </div>

    <b-alert v-if="error" show variant="danger" data-test="error">
      {{ error }}
      <a href="#" class="alert-link ml-1" @click.prevent="load">Try again</a>
    </b-alert>

    <b-alert
      v-if="result"
      show
      :variant="result.failed.length ? 'warning' : 'success'"
      dismissible
      data-test="result"
      @dismissed="result = null"
    >
      Finalised {{ result.finalised }} timesheet{{
        result.finalised === 1 ? '' : 's'
      }}.
      <ul v-if="result.failed.length" class="mb-0 mt-1">
        <li v-for="f in result.failed" :key="f.id">
          {{ nameFor(f.id) }}: {{ f.message }}
        </li>
      </ul>
    </b-alert>

    <p
      v-if="loading && !rows.length"
      class="text-muted py-4 mb-0"
      data-test="loading"
    >
      Loading…
    </p>

    <!-- Weeks a tradie worked and never sent: nothing to approve yet. -->
    <template v-else-if="filter === 'unsent'">
      <div v-if="!unsent.length" class="empty" data-test="empty">
        <div class="font-weight-bold mb-1">Nothing here</div>
        <p class="text-muted mb-0">{{ emptyText }}</p>
      </div>
      <div v-else class="table-scroll">
        <table class="table table-hover mb-0" data-test="unsent-table">
          <thead>
            <tr>
              <th>Tradie</th>
              <th>Project</th>
              <th>Week</th>
              <th class="text-right">Hours logged</th>
              <th>Told</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(u, i) in unsent" :key="i" :data-test="`unsent-${i}`">
              <td class="font-weight-bold">{{ u.tradie.name }}</td>
              <td>{{ u.project.name }}</td>
              <td>{{ weekLabelFor(u) }}</td>
              <td class="text-right">{{ format(u.minutes) }}</td>
              <td class="text-muted">{{ told(u) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <div v-else-if="!visible.length" class="empty" data-test="empty">
      <div class="font-weight-bold mb-1">{{ emptyTitle }}</div>
      <p class="text-muted mb-0">{{ emptyText }}</p>
    </div>

    <template v-else>
      <div v-if="selectable.length" class="bulk mb-2" data-test="bulk">
        <b-form-checkbox
          :checked="allSelected"
          :indeterminate="selected.length > 0 && !allSelected"
          data-test="select-all"
          @change="toggleAll"
        >
          Select all
        </b-form-checkbox>
        <b-button
          variant="primary"
          size="sm"
          :disabled="!selected.length || finalising"
          data-test="finalise-selected"
          @click="finaliseSelected"
        >
          {{
            finalising
              ? 'Finalising…'
              : `Finalise selected (${selected.length})`
          }}
        </b-button>
      </div>

      <div class="table-scroll">
        <table class="table table-hover mb-0" data-test="table">
          <thead>
            <tr>
              <th style="width: 32px"></th>
              <th>Tradie</th>
              <th>Project</th>
              <th>Week</th>
              <th class="text-right">Hours</th>
              <th>Supervisor</th>
              <th>Status</th>
              <th>Sent</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="r in pageRows"
              :key="r.id"
              class="clickable"
              :data-test="`row-${r.id}`"
              @click="open(r)"
            >
              <td @click.stop>
                <b-form-checkbox
                  v-if="isFinalisable(r.status)"
                  :checked="selected.includes(r.id)"
                  :data-test="`select-${r.id}`"
                  :aria-label="`Select ${r.tradie.name}`"
                  @change="toggle(r.id)"
                />
              </td>
              <td class="font-weight-bold">{{ r.tradie.name }}</td>
              <td>{{ r.project.name }}</td>
              <td>{{ week(r) }}</td>
              <td class="text-right">{{ format(r.total_minutes) }}</td>
              <td>
                {{ r.supervisor ? r.supervisor.name : '–' }}
                <div
                  v-if="r.supervisor && r.supervisor.company"
                  class="text-muted small"
                >
                  {{ r.supervisor.company }}
                </div>
              </td>
              <td>
                <span
                  class="chip"
                  :class="`chip--${meta(r.status).tone}`"
                  :data-test="`status-${r.id}`"
                >
                  {{ meta(r.status).label }}
                </span>
                <span
                  v-if="r.overdue"
                  class="chip chip--bad ml-1"
                  :data-test="`overdue-${r.id}`"
                  >Overdue</span
                >
              </td>
              <td class="text-muted">{{ sent(r) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="visible.length > perPage"
        class="d-flex justify-content-center mt-3"
      >
        <b-pagination
          v-model="page"
          :total-rows="visible.length"
          :per-page="perPage"
          data-test="pagination"
        />
      </div>
    </template>

    <external-timesheet-detail-modal
      :id="detailId"
      @close="detailId = null"
      @changed="load"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import apiToasts from '@/mixins/apiToasts'
import ExternalTimesheetDetailModal from '@/components/timesheets/ExternalTimesheetDetailModal.vue'
import dayjs from 'dayjs'
import { buildExternalExportRows } from '@/utils/externalApproval'
import { downloadRowsAsCsv, downloadRowsAsPdf } from '@/utils/pdfExport'
import {
  FILTERS,
  formatMinutes,
  isFinalisable,
  statusMeta,
  weekLabel,
} from '@/utils/externalTimesheets'

export default {
  name: 'ExternalApprovalsTab',

  components: { ExternalTimesheetDetailModal },

  mixins: [apiToasts],

  data() {
    return {
      rows: [],
      unsent: [],
      loading: false,
      error: '',
      filter: 'ready',
      selected: [],
      detailId: null,
      finalising: false,
      exporting: false,
      result: null,
      filters: FILTERS,
      page: 1,
      perPage: 25,
    }
  },

  computed: {
    counts() {
      const out = {}
      FILTERS.forEach((f) => {
        out[f.key] = this.rows.filter(f.match).length
      })
      out.unsent = this.unsent.length
      return out
    },
    visible() {
      const f = FILTERS.find((x) => x.key === this.filter) || FILTERS[0]
      // Newest week first, then by tradie, so a long list reads in order.
      return this.rows
        .filter(f.match)
        .sort(
          (a, b) =>
            b.week_start.localeCompare(a.week_start) ||
            a.tradie.name.localeCompare(b.tradie.name),
        )
    },
    pageRows() {
      const from = (this.page - 1) * this.perPage
      return this.visible.slice(from, from + this.perPage)
    },
    // Only an approved sheet can be finalised, so only those get a tick box.
    // Select all means the sheets on this page.
    selectable() {
      return this.pageRows.filter((r) => isFinalisable(r.status))
    },
    allSelected() {
      return (
        this.selectable.length > 0 &&
        this.selectable.every((r) => this.selected.includes(r.id))
      )
    },
    emptyTitle() {
      return this.rows.length ? 'Nothing here' : 'No external timesheets yet'
    },
    emptyText() {
      if (!this.rows.length) {
        return 'When a tradie sends their week to a supervisor it appears here.'
      }
      return {
        ready: 'No approved timesheets are waiting to be finalised.',
        awaiting: 'No timesheet is waiting on a supervisor.',
        overdue: 'No supervisor is more than a week late.',
        unsent: 'Every tradie has sent the weeks they worked.',
        rejected: 'No timesheet is waiting for a tradie to amend it.',
        finalised: 'Nothing has been finalised yet.',
        all: 'Nothing to show.',
      }[this.filter]
    },
  },

  watch: {
    // A notification opens this tab on one sheet: ?timesheetId=.
    '$route.query.filter': {
      immediate: true,
      handler(key) {
        if (key && FILTERS.some((f) => f.key === key)) this.filter = key
      },
    },
    '$route.query.timesheetId': {
      immediate: true,
      handler(id) {
        if (id) this.detailId = Number(id)
      },
    },
  },

  created() {
    this.load()
  },

  methods: {
    ...mapActions('externalTimesheet', [
      'fetchExternalTimesheets',
      'fetchUnsentWeeks',
      'finaliseManyExternalTimesheets',
      'fetchExportSheets',
    ]),

    isFinalisable,
    format: formatMinutes,
    meta: statusMeta,

    week(r) {
      return weekLabel(r.week_start, r.week_end)
    },
    weekLabelFor(u) {
      return weekLabel(u.week_start, u.week_end)
    },
    told(u) {
      return u.notified_at
        ? new Date(u.notified_at).toLocaleDateString('en-AU', {
            day: 'numeric',
            month: 'short',
          })
        : 'Not yet'
    },
    sent(r) {
      return r.submitted_at
        ? new Date(r.submitted_at).toLocaleDateString('en-AU', {
            day: 'numeric',
            month: 'short',
          })
        : '–'
    },
    nameFor(id) {
      const r = this.rows.find((x) => x.id === id)
      return r ? `${r.tradie.name} (${this.week(r)})` : `Timesheet ${id}`
    },

    async load() {
      this.loading = true
      this.error = ''
      try {
        const [rows, unsent] = await Promise.all([
          this.fetchExternalTimesheets(),
          // A failure here must not hide the main list.
          this.fetchUnsentWeeks().catch(() => []),
        ])
        this.rows = rows
        this.unsent = unsent
        // A shorter list can leave the page past its end.
        const last = Math.max(1, Math.ceil(this.visible.length / this.perPage))
        if (this.page > last) this.page = last
        // A sheet that moved on since the last load can no longer be selected.
        const ok = this.rows
          .filter((r) => isFinalisable(r.status))
          .map((r) => r.id)
        this.selected = this.selected.filter((id) => ok.includes(id))
      } catch (err) {
        this.error = this.errorMessage(
          err,
          'Could not load the external timesheets.',
        )
      } finally {
        this.loading = false
      }
    },

    // Exports the sheets the current filter shows, with the hours as they
    // stand on each sheet, in the same layout as the external supervisor's
    // own export.
    async exportList(format) {
      if (this.exporting) return
      this.exporting = true
      try {
        const shown = new Set(this.visible.map((r) => r.id))
        const sheets = (await this.fetchExportSheets()).filter((s) =>
          shown.has(s.id),
        )
        const rows = buildExternalExportRows(
          sheets.map((s) => ({
            ...s,
            project_name: s.project && s.project.name,
            tradie_name: s.tradie && s.tradie.name,
            decision: s.decision || (s.approved_by_admin ? 'approved' : null),
          })),
        )
        const filename = `External timesheets ${dayjs().format('DD MM YYYY')}`
        if (format === 'pdf') downloadRowsAsPdf(filename, rows)
        else downloadRowsAsCsv(filename, rows)
      } catch (err) {
        this.onError(err, 'Could not export the timesheets.')
      } finally {
        this.exporting = false
      }
    },

    setFilter(key) {
      this.filter = key
      this.selected = []
      this.page = 1
    },
    open(r) {
      this.detailId = r.id
    },
    toggle(id) {
      this.selected = this.selected.includes(id)
        ? this.selected.filter((x) => x !== id)
        : [...this.selected, id]
    },
    toggleAll() {
      this.selected = this.allSelected ? [] : this.selectable.map((r) => r.id)
    },

    async finaliseSelected() {
      if (!this.selected.length || this.finalising) return
      const count = this.selected.length
      const ok = await this.$bvModal.msgBoxConfirm(
        `Finalise ${count} ${
          count === 1 ? 'timesheet' : 'timesheets'
        }? Their hours go to payroll straight away.`,
        {
          title: 'Finalise Timesheets',
          okTitle: `Finalise ${count}`,
          okVariant: 'primary',
          cancelTitle: 'Cancel',
          centered: true,
        },
      )
      if (!ok) return
      this.finalising = true
      try {
        const res = await this.finaliseManyExternalTimesheets({
          ids: this.selected,
        })
        this.result = {
          finalised: (res.finalised || []).length,
          failed: res.failed || [],
        }
        this.selected = []
        await this.load()
      } catch (err) {
        this.onError(err, 'Could not finalise the selected timesheets.')
      } finally {
        this.finalising = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.intro {
  max-width: 640px;
}

.pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.pill {
  font: inherit;
  font-weight: 600;
  font-size: 14px;
  padding: 7px 14px;
  border-radius: 999px;
  border: 1px solid #d5dbe2;
  background: #fff;
  color: #4b5563;
  cursor: pointer;

  &--on {
    background: #e6eefc;
    border-color: #1f5fe0;
    color: #1f5fe0;
  }

  &__count {
    margin-left: 6px;
    font-variant-numeric: tabular-nums;
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 2px solid #1f5fe0;
    outline-offset: 2px;
  }
}

.bulk {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  background: #f4f6fa;
  border-radius: 10px;
}

.table-scroll {
  overflow-x: auto;
  background: #fff;
  border-radius: 10px;
}

.clickable {
  cursor: pointer;
}

.empty {
  text-align: center;
  padding: 2.5rem 1rem;
  background: #fff;
  border: 1px dashed #d5dbe2;
  border-radius: 12px;
}

.chip {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 700;
  white-space: nowrap;

  &--wait {
    background: #fdf0d9;
    color: #9a5b00;
  }
  &--ok {
    background: #e3f4ea;
    color: #1d7a46;
  }
  &--bad {
    background: #fbe6e4;
    color: #b3261e;
  }
  &--done {
    background: #e6eefc;
    color: #1f5fe0;
  }
}
</style>
